
import { useEffect, useState } from "react";
import { Modal, Form, Button, Alert, Spinner, Row, Col } from "react-bootstrap";
import http from "services/httpClient";

interface LabelOption {
  column_index: number;
  label: string;
  unit?: string;
}

interface BooleanIndicatorPropertiesProps {
  show: boolean;
  onClose: () => void;
  config: any;
  deviceId: number;
  refetchGraphs: () => void;
}

const BooleanIndicatorProperties = ({
  show,
  onClose,
  config,
  deviceId,
  refetchGraphs,
}: BooleanIndicatorPropertiesProps) => {
  const [labels, setLabels] = useState<LabelOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [columnIndex, setColumnIndex] = useState("");
  const [activeValue, setActiveValue] = useState("1");
  const [activeLabel, setActiveLabel] = useState("ON");
  const [inactiveLabel, setInactiveLabel] = useState("OFF");
  const [activeColor, setActiveColor] = useState("#198754");
  const [inactiveColor, setInactiveColor] = useState("#dc3545");

  useEffect(() => {
    if (!show || !deviceId) return;

    const loadProperties = async () => {
      setLoading(true);
      setError("");

      try {
        const [rawRes, calcRes, statRes] = await Promise.all([
          http.get(`/device/${deviceId}/labels`),
          http.get(`/calculated_params/${deviceId}`),
          http.get(`/stat-definitions/device/${deviceId}`),
        ]);

        const rawLabels: LabelOption[] = (rawRes.data || [])
          .filter((item: any) => item.is_enabled !== false)
          .map((item: any) => ({
            column_index: Number(item.column_index),
            label: item.label,
            unit: item.unit,
          }));

        const calculatedLabels: LabelOption[] = (calcRes.data || []).map(
          (item: any) => ({
            column_index: Number(item.column_index),
            label: item.result_label,
            unit: item.unit,
          })
        );

        const statisticalLabels: LabelOption[] = (statRes.data || []).map(
          (item: any) => ({
            column_index: 1000 + Number(item.id),
            label: item.result_label,
            unit: item.unit,
          })
        );

        const allLabels = [
          ...rawLabels,
          ...calculatedLabels,
          ...statisticalLabels,
        ].filter(
          (item, index, array) =>
            item.label &&
            array.findIndex(
              (other) => other.column_index === item.column_index
            ) === index
        );

        setLabels(allLabels);

        const savedIndex =
          config.device_label?.column_index ??
          config.column_index ??
          config.source_column_index;

        const initialIndex =
          savedIndex != null &&
          allLabels.some(
            (item) => item.column_index === Number(savedIndex)
          )
            ? String(savedIndex)
            : allLabels.length > 0
              ? String(allLabels[0].column_index)
              : "";

        setColumnIndex(initialIndex);
        setActiveValue(String(config.active_value ?? 1));
        setActiveLabel(config.active_label ?? "ON");
        setInactiveLabel(config.inactive_label ?? "OFF");
        setActiveColor(config.active_color ?? "#198754");
        setInactiveColor(config.inactive_color ?? "#dc3545");
      } catch (err) {
        console.error("Failed to load Boolean Indicator labels:", err);
        setError("Failed to load labels. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadProperties();
  }, [show, deviceId, config]);

  const selectedLabel = labels.find(
    (item) => item.column_index === Number(columnIndex)
  );

  const handleSave = async () => {
    if (!selectedLabel) {
      setError("Please select a label.");
      return;
    }

    setSaving(true);
    setError("");
    
    const selectedColumn = selectedLabel.column_index;

    const updatedConfig = {
      ...config,

      // Display name
      title: selectedLabel.label,

      // Keep both fields synchronized for compatibility
      column_index: selectedColumn,
      label_column_index: selectedColumn,

      device_label: {
        column_index: selectedColumn,
        label: selectedLabel.label,
        unit: selectedLabel.unit ?? "",
      },

      active_value: Number(activeValue),
      active_label: activeLabel,
      inactive_label: inactiveLabel,
      active_color: activeColor,
      inactive_color: inactiveColor,
    };

    try {
      await http.patch(`/graphs/${config.id}`, {
        graph_config: updatedConfig,
      });

      onClose();
      refetchGraphs();
    } catch (err: any) {
      console.error("Failed to save Boolean Indicator:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to save properties. Please check the API request."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Boolean Indicator Properties</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            {error && (
              <Alert variant="danger" className="py-2">
                {error}
              </Alert>
            )}

            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Select Label</Form.Label>
                <Form.Select
                  value={columnIndex}
                  onChange={(e) => setColumnIndex(e.target.value)}
                  disabled={labels.length === 0}
                >
                  {labels.length === 0 ? (
                    <option value="">No labels available</option>
                  ) : (
                    labels.map((item) => (
                      <option
                        key={item.column_index}
                        value={item.column_index}
                      >
                        {item.label} [x{item.column_index}]
                      </option>
                    ))
                  )}
                </Form.Select>
              </Form.Group>

              {selectedLabel && (
                <div className="text-muted small mb-3">
                  Indicator display name: <strong>{selectedLabel.label}</strong>
                  {selectedLabel.unit ? ` (${selectedLabel.unit})` : ""}
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Value considered active</Form.Label>
                <Form.Select
                  value={activeValue}
                  onChange={(e) => setActiveValue(e.target.value)}
                >
                  <option value="1">Logic 1</option>
                  <option value="0">Logic 0</option>
                  {/* <option value="true">Boolean true</option>
                  <option value="false">Boolean false</option> */}
                </Form.Select>
              </Form.Group>

              <Row>
                <Col xs={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Active Label</Form.Label>
                    <Form.Control
                      value={activeLabel}
                      onChange={(e) => setActiveLabel(e.target.value)}
                      maxLength={30}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Inactive Label</Form.Label>
                    <Form.Control
                      value={inactiveLabel}
                      onChange={(e) => setInactiveLabel(e.target.value)}
                      maxLength={30}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col xs={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Active Color</Form.Label>
                    <Form.Control
                      type="color"
                      value={activeColor}
                      onChange={(e) => setActiveColor(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Inactive Color</Form.Label>
                    <Form.Control
                      type="color"
                      value={inactiveColor}
                      onChange={(e) => setInactiveColor(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={loading || saving || !selectedLabel}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BooleanIndicatorProperties;
